import { type ComputedRef, computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'

import { LEGAL } from '~/scopes/legal/route-names'

import { footerConfigs } from '../config/footerConfigs'
import type {
  FooterColumn,
  FooterConsentLink,
  FooterExternalLink,
  FooterLink,
  FooterRouteLink,
  ResolvedFooterColumn,
  ResolvedFooterLink,
} from '../config/types'
import translations from '../translations'

function isRouteLink(link: FooterLink): link is FooterRouteLink {
  return 'route' in link
}

function isExternalLink(link: FooterLink): link is FooterExternalLink {
  return 'href' in link
}

function isConsentLink(link: FooterLink): link is FooterConsentLink {
  return 'action' in link
}

function isFeatureFlagActive(
  experiments: Record<string, string>,
  featureFlag: { name: string; value: string },
): boolean {
  return experiments[featureFlag.name] === featureFlag.value
}

function resolveLink(
  link: FooterLink,
  isDidomiEnabled: boolean,
  showCookiesModal: () => void,
): ResolvedFooterLink {
  const label = translations[link.translationKey]

  if (isConsentLink(link)) {
    if (isDidomiEnabled) {
      return { label, action: showCookiesModal }
    }

    return { label, to: { name: LEGAL.COOKIES } }
  }

  if (isExternalLink(link)) {
    return { label, href: link.href }
  }

  if (isRouteLink(link)) {
    return {
      label,
      to: link.route,
      ...(link.prefetch === false && { prefetch: false }),
    }
  }

  return { label }
}

function resolveColumn(
  column: FooterColumn,
  experiments: Record<string, string>,
  isDidomiEnabled: boolean,
  showCookiesModal: () => void,
): ResolvedFooterColumn {
  const links = (column.links ?? [])
    .filter((link) => {
      if (!('featureFlag' in link) || !link.featureFlag) {
        return true
      }

      return isFeatureFlagActive(experiments, link.featureFlag)
    })
    .map((link) => resolveLink(link, isDidomiEnabled, showCookiesModal))

  return {
    links,
    ...(column.titleKey && { title: translations[column.titleKey] }),
    ...(column.payment && {
      payment: {
        label: translations[column.payment.translationKey],
        ...(column.payment.href && { href: column.payment.href }),
      },
    }),
    ...(column.socials && { socials: column.socials }),
    ...(column.logos && { logos: column.logos }),
  }
}

export function useFooterConfig(): {
  columns: ComputedRef<ResolvedFooterColumn[]>
} {
  const { market } = useMarketplace()
  const experiments = useExperiments()
  const { isDidomiEnabled, showCookiesModal } = useDataConsent()

  const columns = computed<ResolvedFooterColumn[]>(() => {
    const config = footerConfigs[market.countryCode]

    if (!config) {
      return []
    }

    return config.columns.map((column) =>
      resolveColumn(column, experiments, isDidomiEnabled, showCookiesModal),
    )
  })

  return { columns }
}
